import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button as AriaButton } from "react-aria-components";
import { AlertTriangle, ChevronRight, Info, Link2 } from "lucide-react";
import type { ManualState } from "@arc/contracts";
import { NavButton } from "@/components/ui/button";
import { ManualIcon } from "@/components/icons";
import { cn } from "@/lib/utils";
import { manualDetailsHref } from "./drilldown/nav-state.js";
import { HighlightedText } from "./HighlightedText.js";
import { ManualStateBadge, parseMarkComment } from "./ManualStateBadge.js";

/**
 * The manual-reference-element (owner's term, 2026-11-02): a manual shown OUTSIDE the manual selection, pointing to it.
 * It is the ONE component for that in every view (Manual favourites, the dashboard, search results, linked manuals), so
 * a view may only add or leave out information through these props, never restyle it. The manual selection itself uses
 * the manual element ({@link ManualCard}).
 *
 * Always: the manual symbol strip, name + state tag, a "linked" tag on a linked manual, where the manual lives, the report
 * text of a reported manual, and the "Öffnen" link. It shares the shape of a facility card; the strip tells them apart.
 *
 * The action drops under the text at full width when the ELEMENT is narrow, not the page: the same layout then works on a
 * phone, in a dashboard card and in the details drawer.
 */
export function ManualReferenceElement({
  manual,
  path,
  onOpen,
  query = "",
  description,
  badge,
  note,
  actions,
}: {
  manual: {
    id: number;
    name: string;
    state: ManualState;
    markedComment?: string | null;
    parentManualId?: number | null;
    facilityId: number;
  };
  /** Where the manual lives, e.g. "Root / Parent / Facility". */
  path: string;
  /** Absent ⇒ no "Öffnen" (a linked manual the caller can't open). */
  onOpen?: (facilityId: number, manualId: number) => void;
  /** Highlight this term in the name and description (search). */
  query?: string;
  /** Plain text, truncated with a chevron to expand it. Only where the description matters (the Descriptions search). */
  description?: string | null;
  /** One more small tag beside the state, e.g. "Archiviert". */
  badge?: ReactNode;
  /** A line about why something isn't possible here, e.g. why a linked copy can't be restored yet. */
  note?: string;
  /** Further controls in front of "Öffnen", e.g. unlink. */
  actions?: ReactNode;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // Parsed: the column holds a JSON envelope `{ text, section, step }`, never to be shown raw.
  const report = manual.state === "marked" && manual.markedComment ? parseMarkComment(manual.markedComment).text : null;
  return (
    <div className="@container">
      <div className="bg-background border-card flex min-h-[66px] overflow-hidden rounded-xl border shadow-[0_1px_2px_rgb(18_36_52/0.05),0_2px_8px_rgb(18_36_52/0.04)] transition-shadow hover:shadow-md">
        {/* The manual symbol as a full-height strip: it marks "this is a manual" at a glance. A manual's own image can take
            this place once manuals have one. */}
        <span aria-hidden="true" className="bg-muted text-foreground inline-flex w-10 shrink-0 items-center justify-center @md:w-14">
          <ManualIcon className="size-5" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-3 p-3 @md:flex-row @md:items-center @md:gap-3.5">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="m-0 max-w-full min-w-0 truncate text-[15px] leading-5 font-semibold" title={manual.name}>
              <HighlightedText text={manual.name} query={query} />
            </h3>
            <ManualStateBadge state={manual.state} comment={manual.markedComment} />
            {manual.parentManualId != null ? (
              <span className="bg-muted text-muted-foreground inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[0.7rem] font-medium">
                <Link2 aria-hidden="true" className="size-3" />
                {t("manualManagement.badge.linked")}
              </span>
            ) : null}
            {badge}
          </div>
          <p className="text-muted-foreground m-0 truncate text-[13px] leading-[18px]" title={path}>
            {path}
          </p>
          {report ? (
            <p className="text-destructive m-0 flex min-w-0 items-center gap-1.5 text-[13px] leading-[18px]" title={report}>
              <AlertTriangle aria-hidden="true" className="size-3.5 shrink-0" />
              <span className="truncate">{report}</span>
            </p>
          ) : null}
          {note ? (
            <p className="text-muted-foreground m-0 flex min-w-0 items-start gap-1.5 text-[13px] leading-[18px]">
              <Info aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              <span>{note}</span>
            </p>
          ) : null}
          {description ? (
            <AriaButton
              aria-expanded={expanded}
              aria-label={t(expanded ? "manualManagement.badge.collapseDescription" : "manualManagement.badge.expandDescription", { name: manual.name })}
              onPress={() => setExpanded((v) => !v)}
              className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 flex w-full cursor-pointer items-start gap-1 rounded border-0 bg-transparent p-0 text-left text-xs outline-none focus-visible:ring-2"
            >
              <ChevronRight aria-hidden="true" className={cn("mt-0.5 size-3.5 shrink-0 transition-transform", expanded && "rotate-90")} />
              <span className={cn("min-w-0", !expanded && "line-clamp-1")}>
                <HighlightedText text={description} query={query} />
              </span>
            </AriaButton>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5 empty:hidden">
          {actions}
          {onOpen ? (
            <NavButton
              variant="accent"
              className="h-[38px] flex-1 rounded-[10px] px-3 text-[13px] font-semibold @md:flex-none"
              aria-label={t("manualManagement.badge.open", { name: manual.name })}
              href={manualDetailsHref(manual.facilityId, manual.id)}
              onNavigate={() => onOpen(manual.facilityId, manual.id)}
            >
              <ManualIcon aria-hidden="true" />
              {t("manualManagement.badge.openButton")}
            </NavButton>
          ) : null}
        </div>
        </div>
      </div>
    </div>
  );
}
